"use client";

import { useEffect, useRef, type RefObject } from "react";
import { useTranslations } from "use-intl";

import { CloseIcon } from "@/components/appIcons";
import { cn } from "@/lib/utils";

import { SearchFilters, type SearchFiltersProps } from "./search-filters";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ResponsiveSearchFilters({
  open,
  filterProps,
  hasFilters,
  resetFilters,
  onClose,
  triggerRef,
}: {
  open: boolean;
  filterProps: SearchFiltersProps;
  hasFilters: boolean;
  resetFilters: () => void;
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
}) {
  const t = useTranslations("globals.search");
  const panelRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = [...panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      triggerRef.current?.focus();
    };
  }, [onClose, open, triggerRef]);

  return (
    <>
      {open ? (
        <button
          type="button"
          aria-label={t("showResults")}
          className="fixed inset-0 z-[60] bg-black/60 md:hidden"
          onClick={onClose}
        />
      ) : null}
      <aside
        id="search-filters"
        ref={panelRef}
        role={open ? "dialog" : undefined}
        aria-modal={open || undefined}
        aria-label={t("filters")}
        data-search-filter-tree
        className={cn(
          "w-56 flex-shrink-0 bg-background no-scrollbar",
          "md:sticky md:top-24 md:z-[15] md:mr-8 md:block md:max-h-[calc(100svh-6rem-3.5rem)] md:overflow-y-auto xl:max-h-[calc(100svh-6rem-2rem)]",
          "max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:top-12 max-md:z-[61] max-md:w-auto max-md:overflow-y-auto max-md:rounded-t-2xl",
          open
            ? "max-md:block max-md:animate-in max-md:slide-in-from-bottom max-md:duration-200"
            : "max-md:hidden",
        )}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-5 py-4 md:static md:border-0 md:px-0 md:pb-0 md:pt-0">
          <h2 className="text-lg font-bold uppercase tracking-wider">{t("filters")}</h2>
          <div className="flex items-center gap-4">
            {hasFilters ? (
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs font-bold uppercase tracking-widest text-primary hover:underline"
              >
                {t("reset")}
              </button>
            ) : null}
            <button
              ref={closeButtonRef}
              type="button"
              aria-label={t("showResults")}
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground md:hidden"
            >
              <CloseIcon aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="px-5 py-4 md:px-0 md:pb-8 md:pt-6">
          <SearchFilters {...filterProps} />
        </div>
        <div className="sticky bottom-0 border-t border-border bg-background px-5 py-4 md:hidden">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-primary py-3 font-bold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("showResults")}
          </button>
        </div>
      </aside>
    </>
  );
}
